import React, { useMemo } from "react";
import { cn } from "@danishagro/shared/src/helpers/classNames.helper";
import { getAbsoluteUrl } from "@danishagro/shared/src/components/organisms/SiteHeader/LanguageSelector/hooks/getAbsolutePath.hook";
import { DA_Image } from "@danishagro/shared/src/components/atoms/Image/Image.component";
import {
    AddressType,
    TextType,
    LinkType,
    SoMeType,
} from "@danishagro/shared/src/interfaces/footerObject.interface";
import { Helmet } from "react-helmet";
import { DA_FooterObject } from "../../../../interfaces/footerObject.interface";
import { DA_BasicLink } from "../../../atoms/BasicLink/BasicLink.component";
import { DA_Icon, DA_IconNames } from "../../../atoms/Icon/Icon.component";
import { DA_Container } from "../../../atoms/Container/Container.component";
import { _FooterLink } from "../FooterLink/FooterLink.component";
import S from "./DesktopFooter.module.scss";

export interface DA_DesktopFooterProps {
    data: DA_FooterObject;
    isMiniFooter?: boolean;
}

export const DA_DesktopFooter = ({ data, isMiniFooter = false }: DA_DesktopFooterProps) => {
    const isFooterAddress = (
        item: TextType | LinkType | AddressType | SoMeType
    ): item is AddressType => {
        return item.itemType === "footerAddress";
    };

    // set width to 33,33....% on columns when columns.length === 3
    const columnWidth = data.columns?.length;
    const setColumnWidth = useMemo(() => {
        if (columnWidth === 3) return S.columnThreeWidth;
    }, [columnWidth]);

    return (
        <footer className={cn(S.desktopFooter, isMiniFooter && S.miniFooter)}>
            <DA_Container className={S.desktopFooterContainer}>
                <div className={S.desktopFooterInner}>
                    {data?.columns?.map((column, index) => (
                        <div key={index} className={cn(S.desktopFooterColumn, setColumnWidth)}>
                            <div className={S.desktopFooterItem}>
                                <span className={S.desktopFooterItemTitle}>{column.header}</span>
                                {column?.items?.map((item, index) => {
                                    if (item.itemType === "footerLinkImage") {
                                        return (
                                            <div key={index}>
                                                <DA_BasicLink
                                                    href={item.link?.url}
                                                    target={item.link?.target}
                                                    title={item.link?.name}
                                                >
                                                    <DA_Image
                                                        src={getAbsoluteUrl(
                                                            item.image?.props?.umbracoFile
                                                        )}
                                                        alt={item.image?.name}
                                                        className={S.footerLinkImage}
                                                    />
                                                </DA_BasicLink>
                                            </div>
                                        );
                                    }
                                    if (item.itemType === "paymentTypes") {
                                        const iconMap: Record<string, string> = {
                                            dankort: "/images/payment-options/b2c-dankort.png",
                                            visa: "/images/payment-options/b2c-visa.png",
                                            mastercard:
                                                "/images/payment-options/b2c-mastercard.png",
                                            mobilepay: "/images/payment-options/b2c-mobilepay.png",
                                        };

                                        return (
                                            <div key={index}>
                                                <div className={S.paymentTypes}>
                                                    {item.paymenticons.map((icon, index) =>
                                                        iconMap[icon] ? (
                                                            <img
                                                                key={index}
                                                                src={iconMap[icon]}
                                                                alt={icon}
                                                            />
                                                        ) : null
                                                    )}
                                                </div>
                                                <div>{item.text}</div>
                                            </div>
                                        );
                                    }
                                    if (item.itemType === "text") {
                                        // If both "title" and "text" exist: place them next to each other like columns
                                        // If only "title" exists: CSS will make sure it uses the full width
                                        // If only "text" exists: don't prevent the text from wrapping
                                        return (
                                            <div key={index} className={S.footerItemWrapper}>
                                                {item.title ? (
                                                    <span
                                                        className={S.title}
                                                        dangerouslySetInnerHTML={{
                                                            __html: item.title,
                                                        }}
                                                    />
                                                ) : null}
                                                {item.text ? (
                                                    <span
                                                        className={
                                                            item.title ? S.noWrap : undefined
                                                        }
                                                        dangerouslySetInnerHTML={{
                                                            __html: item.text,
                                                        }}
                                                    />
                                                ) : null}
                                            </div>
                                        );
                                    }
                                    if (isFooterAddress(item)) {
                                        const schemaMarkup = {
                                            "@context": "https://schema.org",
                                            "@type": "Organization",
                                            name: item.company,
                                            address: {
                                                "@type": "PostalAddress",
                                                streetAddress: item.address1,
                                                addressLocality: item.zipCity.split(" ")[0],
                                                postalCode: item.zipCity.split(" ")[1],
                                            },
                                            iso6523Code: item.cvr,
                                            email: item.email,
                                            telephone: item.phone,
                                        };

                                        return (
                                            <div key={index} className={S.addressInfoWrapper}>
                                                <Helmet>
                                                    <script type="application/ld+json">
                                                        {JSON.stringify(schemaMarkup)}
                                                    </script>
                                                </Helmet>
                                                {item.company && (
                                                    <span
                                                        className={S.addressInfo}
                                                        dangerouslySetInnerHTML={{
                                                            __html: item.company,
                                                        }}
                                                    />
                                                )}
                                                {item.address1 && (
                                                    <span
                                                        className={S.addressInfo}
                                                        dangerouslySetInnerHTML={{
                                                            __html: item.address1,
                                                        }}
                                                    />
                                                )}
                                                {item.address2 && (
                                                    <span
                                                        className={S.addressInfo}
                                                        dangerouslySetInnerHTML={{
                                                            __html: item.address2,
                                                        }}
                                                    />
                                                )}
                                                {item.zipCity && (
                                                    <span className={S.addressInfo}>
                                                        <span>{item.zipCity.split(" ")[0]}</span>
                                                        <span>{` ${
                                                            item.zipCity.split(" ")[1]
                                                        }`}</span>
                                                    </span>
                                                )}
                                                {item.country && (
                                                    <span className={S.addressInfo}>
                                                        {item.country}
                                                    </span>
                                                )}
                                                {item.cvr && (
                                                    <span className={S.addressInfo}>
                                                        {item.cvr}
                                                    </span>
                                                )}
                                                {item.email && (
                                                    <span className={S.addressInfo}>
                                                        {item.email}
                                                    </span>
                                                )}
                                                {item.phone && (
                                                    <span className={S.addressInfo}>
                                                        {item.phone}
                                                    </span>
                                                )}
                                            </div>
                                        );
                                    }
                                    if (item.itemType === "link") {
                                        return (
                                            <div key={index} className={S.footerItemWrapper}>
                                                <_FooterLink
                                                    key={index}
                                                    {...item}
                                                    classname={S.footerLink}
                                                />
                                            </div>
                                        );
                                    }
                                })}
                            </div>
                        </div>
                    ))}
                </div>

                {data?.socials?.map((socials, index) => (
                    <div key={index} className={cn(S.desktopFooterItem, S.noMarginBottom)}>
                        <span className={S.desktopFooterItemTitle}>{socials.header}</span>
                        <div className={S.soMeLinks}>
                            {socials.items.map((item) => {
                                let iconName;

                                switch (item.type) {
                                    case "facebook":
                                        iconName = DA_IconNames.Facebook;
                                        break;
                                    case "linkedin":
                                        iconName = DA_IconNames.LinkedIn;
                                        break;
                                    case "instagram":
                                        iconName = DA_IconNames.Instagram;
                                        break;
                                    case "youtube":
                                        iconName = DA_IconNames.YouTube;
                                        break;
                                    case "tiktok":
                                        iconName = DA_IconNames.TikTok;
                                        break;
                                    default:
                                        iconName = DA_IconNames.Question;
                                }

                                return (
                                    <DA_BasicLink
                                        href={item.href}
                                        key={item.type}
                                        className={S.soMeLink}
                                        ariaLabel={item.type}
                                        target="_blank"
                                    >
                                        <DA_Icon name={iconName} />
                                    </DA_BasicLink>
                                );
                            })}
                        </div>
                    </div>
                ))}
            </DA_Container>
        </footer>
    );
};
